/*
 * Discovery landing pages — email sign-up.
 *
 * Subscribes to the Klaviyo list through Klaviyo's client subscription API (public key only),
 * using the same list + $source convention as the live Pinterest forms:
 *   - $source = "pinterest_organic" when the visit came from an organic Pinterest pin
 *     (feeds the existing "Pinterest organic" segment, YyAnVm). Nothing is written to
 *     $source on untagged visits, so a later untagged visit never overwrites it.
 *   - The subscription's custom_source records page + form (+ pin) on the consent record
 *     itself, so the original sign-up source stays distinct from later touches.
 *   - "Discovery …" profile properties record the pin's UTMs, page, entry chapter and form.
 *   Klaviyo stores custom_source as the profile's $source, so "pinterest_organic" above does
 *   not persist; the "Pinterest Organic" = true property is what the segment relies on.
 *
 * After an accepted sign-up, any [data-dsc-after] element in the same invitation is revealed
 * (optional product / Journal exploration beneath the confirmation).
 *
 * New subscribers are counted from Klaviyo's "Subscribed to List" metric, which only fires
 * for a real new subscription — never from a button click. A 202 here means Klaviyo accepted
 * the request; it does not distinguish new from existing subscribers, so the page shows the
 * same message either way and never reveals subscription status.
 */
(function () {
  if (window.__dscSignup) return;
  window.__dscSignup = true;

  var TOUCH_KEY = 'blb_discovery_touch';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var REVISION = '2025-01-15';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function readTouch() {
    try { return JSON.parse(sessionStorage.getItem(TOUCH_KEY) || 'null'); } catch (e) { return null; }
  }
  function saveTouch(t) {
    try { sessionStorage.setItem(TOUCH_KEY, JSON.stringify(t)); } catch (e) {}
  }

  // The chapter the visitor landed on: the URL fragment (#calendula) if it names a chapter,
  // otherwise the first chapter on the page (the opening story).
  function landingEntry() {
    var chapters = document.querySelectorAll('[data-dsc-botanical]');
    if (!chapters.length) return '';
    var hash = '';
    try { hash = decodeURIComponent((location.hash || '').slice(1)); } catch (e) {}
    for (var i = 0; i < chapters.length; i++) {
      if (hash && chapters[i].id === hash) return chapters[i].getAttribute('data-dsc-botanical');
    }
    return chapters[0].getAttribute('data-dsc-botanical');
  }

  var touch = { page: '', utm: {}, entry: '' };
  var pageKey = '';

  // Capture the touch once per page load. A tagged arrival replaces the stored touch;
  // an untagged reload in the same session keeps the earlier tagged touch for this page.
  function captureTouch() {
    var params = new URLSearchParams(location.search);
    var utm = {};
    UTM_KEYS.forEach(function (k) { var v = params.get(k); if (v) utm[k] = v.slice(0, 200); });
    var first = document.querySelector('[data-dsc-form]');
    pageKey = first ? first.getAttribute('data-page') : '';
    if (Object.keys(utm).length) {
      touch = { page: pageKey, utm: utm, entry: landingEntry() };
      saveTouch(touch);
    } else {
      var stored = readTouch();
      touch = stored && stored.page === pageKey ? stored : { page: pageKey, utm: {}, entry: landingEntry() };
    }
  }

  function buildBody(email, root) {
    var placement = root.getAttribute('data-placement');
    var u = touch.utm || {};
    var props = {
      'Discovery Page': pageKey,
      'Discovery Entry': touch.entry || '',
      'Discovery Form': placement
    };
    UTM_KEYS.forEach(function (k) { if (u[k]) props['Discovery ' + k] = u[k]; });
    if (u.utm_source === 'pinterest' && u.utm_medium === 'organic_social') {
      props['$source'] = 'pinterest_organic';
      // Only ever set to true, never false, so a later untagged visit can't clear it.
      // The "Pinterest organic" segment (YyAnVm) matches on this property as well as $source.
      props['Pinterest Organic'] = true;
    }
    var detail = 'Discovery: ' + pageKey + ' / ' + placement + (u.utm_content ? ' / ' + u.utm_content : '');
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: detail.slice(0, 250),
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties: props
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: root.getAttribute('data-list') } } }
      }
    };
  }

  function setup(root) {
    var form = root.querySelector('form');
    var input = root.querySelector('input[type="email"]');
    var button = root.querySelector('button[type="submit"]');
    var status = root.querySelector('.dsc-form__status');
    if (!form || !input || !button || !status) return;
    var buttonLabel = button.textContent;
    var busy = false;

    function show(msg, kind) {
      status.textContent = msg || '';
      status.classList.toggle('dsc-form__status--error', kind === 'error');
      status.classList.toggle('dsc-form__status--ok', kind === 'ok');
    }
    function invalid(msg) {
      input.setAttribute('aria-invalid', 'true');
      show(msg, 'error');
      input.focus();
    }
    function reset() {
      busy = false;
      button.disabled = false;
      button.textContent = buttonLabel;
      form.removeAttribute('aria-busy');
    }
    input.addEventListener('input', function () {
      input.removeAttribute('aria-invalid');
      if (status.classList.contains('dsc-form__status--error')) show('', '');
    });

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (busy) return;
      var email = input.value.trim();
      if (!email) return invalid(root.getAttribute('data-msg-empty'));
      if (!EMAIL_RE.test(email) || !input.checkValidity()) return invalid(root.getAttribute('data-msg-invalid'));

      busy = true;
      button.disabled = true;
      button.textContent = root.getAttribute('data-msg-sending');
      form.setAttribute('aria-busy', 'true');
      show('', '');

      var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(root.getAttribute('data-company'));
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION },
        body: JSON.stringify(buildBody(email, root))
      })
        .then(function (res) {
          if (!res.ok) throw new Error('status ' + res.status);
          form.removeAttribute('aria-busy');
          var row = root.querySelector('.dsc-form__row');
          var label = root.querySelector('.dsc-form__label');
          if (row) row.hidden = true;
          if (label) label.hidden = true;
          show(root.getAttribute('data-msg-ok'), 'ok');
          status.setAttribute('tabindex', '-1');
          status.focus({ preventScroll: true });
          var invite = root.closest('.dsc-invite');
          var after = invite ? invite.querySelector('[data-dsc-after]') : null;
          if (after) after.hidden = false;

          var detail = {
            page: pageKey,
            entry: touch.entry || '',
            placement: root.getAttribute('data-placement'),
            utm: touch.utm || {}
          };
          // "Accepted", not "new subscriber" — new subscribers are counted in Klaviyo.
          document.dispatchEvent(new CustomEvent('blb:discovery-signup-accepted', { detail: detail }));
          try {
            if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
              Shopify.analytics.publish('blb_discovery_signup_accepted', detail);
            }
          } catch (e) {}
        })
        .catch(function () {
          // The entered email stays in the field for a retry.
          show(root.getAttribute('data-msg-failed'), 'error');
          reset();
        });
    });
  }

  function init() {
    captureTouch();
    document.querySelectorAll('[data-dsc-form]').forEach(setup);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
